/**
 * Recensioni Feature React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import * as api from '@/api/queries/recensioni';
import type { Recensione, RecensioneInsert, RecensioneUpdate } from './types';

const QUERY_KEY = 'recensioni';

// ============================================================
// QUERIES
// ============================================================

export function useRecensioni() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getRecensioni({ userId: user?.id });
      if (error) throw error;
      return (data || []) as Recensione[];
    },
    enabled: !!user?.id,
  });
}

export function useRecensione(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getRecensioneById(id, user?.id);
      if (error) throw error;
      return data as Recensione | null;
    },
    enabled: !!user?.id && !!id,
  });
}

// ============================================================
// MUTATIONS
// ============================================================

export function useCreateRecensione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (recensione: RecensioneInsert): Promise<Recensione> => {
      const { data, error } = await api.createRecensione(recensione);
      if (error) throw error;
      if (!data) throw new Error("Failed to create recensione");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateRecensione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RecensioneUpdate }): Promise<Recensione> => {
      const { data: result, error } = await api.updateRecensione(id, data);
      if (error) throw error;
      if (!result) throw new Error("Failed to update recensione");
      return result;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useDeleteRecensione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await api.deleteRecensione(id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useRecensioniStats() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', user?.id],
    queryFn: async () => {
      const { data, error } = await api.getRecensioni({ userId: user?.id });
      if (error) throw error;
      const recensioni = (data || []) as Recensione[];
      
      return {
        totali: recensioni.length,
        approvate: recensioni.filter(r => r.stato === 'Approvata').length,
        media_stelle: recensioni.length > 0 
          ? recensioni.reduce((sum, r) => sum + (r.valutazione_generale || 0), 0) / recensioni.length 
          : 0,
        bozze: recensioni.filter(r => r.stato === 'Bozza').length,
      };
    },
    enabled: !!user?.id,
  });
}
